import React, { useState, useEffect, useMemo, useRef } from "react";
import Paper from "@mui/material/Paper";
import {
  Box,
  Grid,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { getAggregation } from "../../../api/aggregationAPI";
import {getProjects} from "../../../api/projectAPI";
import { useTranslation } from "react-i18next";
import CurrencyLiraIcon from "@mui/icons-material/CurrencyLira";
import ConvertOIDValueToString from "../../../utils/objectConverter";
import { useLocation } from "react-router-dom";
import { useRefreshInterval } from "../../../contexts/RefreshIntervalContext";

import { getCurrentTenant } from "../../../config/tenantConfig";
import { getPlaceIdFromPathname } from "../../../utils/pathUtils";
import { useObjectData } from "../../../hooks/useLocalStorageData";

const { apiBaseUrl: API_BASE_URL, projectCode: PROJECT_CODE } = getCurrentTenant();
const IS_VENDAI = PROJECT_CODE === "VENDAI";

function WidgetBox({ widget, getAggregation, objectData, nodeId }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(null);
  const [loadingValue, setLoadingValue] = useState(true);
  const { interval, isRunning } = useRefreshInterval();
  const intervalMs = useMemo(
    () => Math.max((interval || 15) * 1000, 1000),
    [interval]
  );

  useEffect(() => {
    let intervalId;
    async function fetchValue() {
      if (!nodeId) {
        setValue(null);
        setLoadingValue(false);
        return;
      }
      try {
        const aggregationResult = await getAggregation(
          widget.request.payload,
          widget.request.endpoint,
          nodeId
        );

        if (Array.isArray(aggregationResult) && aggregationResult.length > 0) {
          const numericValue = aggregationResult[0][widget.targetObject];
          const targetObjectCode = parseInt(widget.targetObject);
          const objectInfo = objectData.find(
            (obj) => obj.code === targetObjectCode
          );

          if (objectInfo) {
            if (
              widget.isConvertableObject === false ||
              objectInfo.isConvertableObject === false
            ) {
              setValue(numericValue ?? null);
            } else {
              const convertedValue = ConvertOIDValueToString(
                {
                  Type: objectInfo.type,
                },
                numericValue
              );
              setValue(convertedValue ?? null);
            }
          } else {
            setValue(numericValue ?? null);
          }
        } else {
          setValue(null);
        }
      } catch (err) {
        console.error("Error fetching aggregation for widget:", err);
        setValue(null);
      } finally {
        setLoadingValue(false);
      }
    }

    fetchValue();
    // Paused: bir kez çekildi, tekrarlı polling kurulmaz.
    if (!isRunning) return undefined;
    intervalId = setInterval(fetchValue, intervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [widget, objectData, getAggregation, nodeId, intervalMs, isRunning]);

  // Sayısal değerleri iki ondalık basamakla göster; sayı değilse olduğu gibi bırak
  const formatAsFloat = (val) => {
    if (val === null || val === undefined) return val;
    const num = typeof val === "number" ? val : Number(val);
    if (!Number.isFinite(num)) return val;
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <Box
      sx={(theme) => ({
        p: { xs: 1, sm: 1.5 },
        borderRadius: 0.5,
        border: `1px solid ${theme.palette.divider}`,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginBottom: { xs: 1, sm: 2 },
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: theme.palette.background.paper,
        minHeight: { xs: 100, sm: "auto" },
      })}
    >
      <Typography variant="subtitle2" color="text.secondary">
        {t(`widgets.${widget.headKey}`)}
      </Typography>

      {loadingValue ? (
        <CircularProgress size={24} sx={{ my: 1 }} />
      ) : (
        <Typography
          variant="h6"
          // fontWeight="bold"
          sx={{
            my: 0.5,
            textAlign: "center",
            fontSize: { xs: "0.95rem", sm: "1.05rem" },
            lineHeight: 1.15,
            fontWeight: 400,
          }}
          color="text.primary"
        >
          {widget.iconName === "CurrencyLiraIcon" && (
            <CurrencyLiraIcon
              fontSize="small"
              sx={{ verticalAlign: "middle", ml: 0.5 }}
            />
          )}
          {value !== null
            ? widget.suffixKey !== null
              ? `${formatAsFloat(value)} ${t(`widgets.${widget.suffixKey}`)}`
              : formatAsFloat(value)
            : "-"}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary">
        {t(`widgets.${widget.titleKey}`)}
      </Typography>
    </Box>
  );
}

function WidgetTable({ widget }) {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState(null);
  const [loadingValue, setLoadingValue] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isFirstLoadRef = useRef(true);
  const { interval, isRunning } = useRefreshInterval();
  const intervalMs = useMemo(
    () => Math.max((interval || 15) * 1000, 1000),
    [interval]
  );

  useEffect(() => {
    let intervalId;
    async function fetchTableData() {
      if (isFirstLoadRef.current) {
        setLoadingValue(true);
      } else {
        setIsRefreshing(true);
      }
      try {
        const authToken = localStorage.getItem("authToken");
        const projectCode = localStorage.getItem("projectCode");

        const endpointParts = widget.request.endpoint.split("api/");
        const apiPath =
          endpointParts.length > 1 ? endpointParts[1] : widget.request.endpoint;

        const pathNodeId = getPlaceIdFromPathname(window.location.pathname);
        const fullUrl = `${API_BASE_URL}/${projectCode}/api/${apiPath}/${pathNodeId}`;

        const response = await fetch(fullUrl, {
          method: widget.request.method || "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result && result.data) {
          const nextData = {
            headers: result.data.headers || [],
            rowHeaders: result.data.rowHeaders || [],
            cells: result.data.cells || [],
            title: result.data.title || "",
          };
          setTableData((prev) => {
            try {
              const prevStr = JSON.stringify(prev ?? {});
              const nextStr = JSON.stringify(nextData);
              if (prevStr === nextStr) return prev;
            } catch (_) {}
            return nextData;
          });
        } else {
          setTableData(
            (prev) =>
              prev ?? {
                headers: [],
                rowHeaders: [],
                cells: [],
                title: "",
              }
          );
        }
      } catch (err) {
        console.error("Error fetching table data for widget:", err);
        if (isFirstLoadRef.current) {
          setTableData({ headers: [], rowHeaders: [], cells: [], title: "" });
        }
      } finally {
        if (isFirstLoadRef.current) {
          setLoadingValue(false);
          isFirstLoadRef.current = false;
        } else {
          setIsRefreshing(false);
        }
      }
    }

    fetchTableData();
    // Paused: bir kez çekildi, tekrarlı polling kurulmaz.
    if (!isRunning) return undefined;
    intervalId = setInterval(fetchTableData, intervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [widget, intervalMs, isRunning]);

  return (
    <Paper
      elevation={2}
      sx={(theme) => ({
        p: 0,
        borderRadius: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginBottom: 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.3)"
            : "0 2px 8px rgba(0,0,0,0.1)",
        border:
          theme.palette.mode === "dark"
            ? `1px solid #2d5016`
            : `1px solid #e8f5e8`,
        transition: "all 0.3s ease-in-out",
        position: "relative",
      })}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          backgroundColor:
            theme.palette.mode === "dark" ? "#4caf50" : "#4caf50",
          color: "white",
          padding: "12px 16px",
        })}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {tableData?.title === "sumSales"
            ? t("widgets.sumSales", "Toplam Satış")
            : tableData?.title === "salesCount"
            ? t("widgets.salesCount", "Satış Adeti")
            : tableData?.title
            ? t(`widgets.${tableData.title}`, tableData.title)
            : ""}
        </Typography>
        {isRefreshing && (
          <CircularProgress size={16} thickness={6} sx={{ color: "white" }} />
        )}
      </Box>
      <TableContainer
        sx={{
          maxWidth: "100%",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow sx={{ height: 44 }}>
              <TableCell
                sx={(theme) => {
                  return {
                    fontWeight: 500,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#0d4f14" : "#c8e6c9",
                    color: theme.palette.text.primary,
                    padding: "4px 8px",
                    fontSize: "0.85rem",
                    borderBottom:
                      theme.palette.mode === "dark"
                        ? "2px solid #0d4f14"
                        : "2px solid #c8e6c9",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    textAlign: "right",
                    width: `${
                      tableData?.headers?.length
                        ? 100 / (tableData.headers.length + 1)
                        : 20
                    }%`,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  };
                }}
              ></TableCell>
              {tableData?.headers?.map((header, index) => {
                let translatedHeader = header;
                if (header === "card")
                  translatedHeader = t("widgets.card", "Kart");
                else if (header === "cash")
                  translatedHeader = t("widgets.cash", "Nakit");
                else if (header === "app")
                  translatedHeader = t("widgets.app", "Mobil");
                else if (header === "sum")
                  translatedHeader = t("widgets.sum", "Toplam");
                return (
                  <TableCell
                    key={index}
                    sx={(theme) => ({
                      fontWeight: 500,
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1b5e20" : "#c8e6c9",
                      color: theme.palette.text.primary,
                      padding: "4px 8px",
                      textAlign: "center",
                      fontSize: "0.85rem",
                      borderBottom:
                        theme.palette.mode === "dark"
                          ? "2px solid #1b5e20"
                          : "2px solid #c8e6c9",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      width: `${
                        tableData?.headers?.length
                          ? 100 / (tableData.headers.length + 1)
                          : 20
                      }%`,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    })}
                  >
                    {translatedHeader}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData?.cells?.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={(theme) => ({
                  height: 44,
                  "&:nth-of-type(even)": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#222b31" : "#f5f5f5",
                  },
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#35596b" : "#e0f2f1",
                  },
                  transition: "background-color 0.2s ease",
                  borderBottom:
                    theme.palette.mode === "dark"
                      ? "1px solid #29434e"
                      : "1px solid #b2dfdb",
                })}
              >
                <TableCell
                  sx={(theme) => ({
                    fontWeight: 500,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#1b5e20" : "#c8e6c9",
                    color: theme.palette.text.primary,
                    padding: "4px 8px",
                    textAlign: "center",
                    fontSize: "0.8rem",
                    borderRight:
                      theme.palette.mode === "dark"
                        ? "1px solid #1b5e20"
                        : "1px solid #c8e6c9",
                    width: `${
                      tableData?.headers?.length
                        ? 100 / (tableData.headers.length + 1)
                        : 20
                    }%`,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  })}
                >
                  {(() => {
                    const rowHeader = tableData?.rowHeaders?.[rowIndex];
                    if (!rowHeader) return `${rowIndex + 1}`;
                    switch (rowHeader) {
                      case "today":
                        return t("widgets.today", "Bugün");
                      case "yesterday":
                        return t("widgets.yesterday", "Dün");
                      case "thisWeek":
                        return t("widgets.thisWeek", "Bu Hafta");
                      case "lastWeek":
                        return t("widgets.lastWeek", "Geçen Hafta");
                      case "thisMonth":
                        return t("widgets.thisMonth", "Bu Ay");
                      case "lastMonth":
                        return t("widgets.lastMonth", "Geçen Ay");
                      case "thisYear":
                        return t("widgets.thisYear", "Bu Yıl");
                      case "lastYear":
                        return t("widgets.lastYear", "Geçen Yıl");
                      default:
                        return rowHeader;
                    }
                  })()}
                </TableCell>
                {row.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    sx={(theme) => ({
                      textAlign: "right",
                      padding: { xs: "8px 12px", sm: "4px 8px" },
                      fontSize: "0.8rem",
                      color:
                        cell !== null
                          ? theme.palette.mode === "dark"
                            ? "#e0f7fa"
                            : "#263238"
                          : theme.palette.mode === "dark"
                          ? "#666"
                          : "#b0b8c1",
                      fontWeight: cell !== null ? "600" : "400",
                      borderRight:
                        cellIndex === row.length - 1
                          ? "none"
                          : theme.palette.mode === "dark"
                          ? "1px solid #29434e"
                          : "1px solid #b2dfdb",
                      width: `${
                        tableData?.headers?.length
                          ? 100 / (tableData.headers.length + 1)
                          : 20
                      }%`,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    })}
                  >
                    {cell !== null ? (
                      <Typography
                        variant="body1"
                        fontWeight={400}
                        sx={(theme) => ({
                          display: "block",
                          width: "100%",
                          textAlign: "right",
                          color:
                            theme.palette.mode === "dark"
                              ? "#b3e5fc"
                              : "#000000",
                          fontSize: "0.9rem",
                          fontFamily: "'Roboto Mono', monospace",
                          lineHeight: 1.1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        })}
                      >
                        {typeof cell === "number"
                          ? cell.toLocaleString()
                          : cell}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body1"
                        fontWeight={400}
                        sx={(theme) => ({
                          display: "block",
                          width: "100%",
                          textAlign: "right",
                          color:
                            theme.palette.mode === "dark" ? "#666" : "#bdbdbd",
                          fontSize: "0.9rem",
                          lineHeight: 1.1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        })}
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {loadingValue && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(18, 18, 18, 0.6)"
                : "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(1.5px)",
            zIndex: 10,
            pointerEvents: "none",
          })}
        >
          <CircularProgress size={32} thickness={4} sx={{ color: "#4caf50" }} />
        </Box>
      )}
    </Paper>
  );
}

function WidgetList({ widget, getAggregation, objectData, nodeId }) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { interval, isRunning } = useRefreshInterval();
  const intervalMs = useMemo(
    () => Math.max((interval || 15) * 1000, 1000),
    [interval]
  );

  const VISIBLE_ROWS = 8;
  const ROW_HEIGHT = 44;
  const HEADER_HEIGHT = 48;

  const columns = useMemo(() => {
    if (!Array.isArray(widget.targetObjectList)) return [];
    const dateColumn = {
      field: "date",
      headerName: t("widgets.date", "Date"),
      flex: 1,
      minWidth: 130,
      sortable: false,
      headerAlign: IS_VENDAI ? "left" : "center",
      align: IS_VENDAI ? "left" : "center",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            width: "100%",
            textAlign: IS_VENDAI ? "left" : "center",
            fontSize: "0.7rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value ?? "-"}
        </Typography>
      ),
    };

    const lang = (
      localStorage.getItem("i18nextLng") ||
      i18n.language ||
      "tr"
    ).split("-")[0].toLowerCase();
    const dynamicColumns = widget.targetObjectList
      .filter((c) => c !== "NID")
      .map((code) => {
        const field = code.toString();
        let headerName = field;
        const numericCode = parseInt(field, 10);
        if (!Number.isNaN(numericCode)) {
          const objInfo = objectData.find((o) => o.code === numericCode);
          if (objInfo) {
            const localizedName =
              lang === "tr"
                ? objInfo.name1 ?? objInfo.name
                : lang === "de"
                ? objInfo.name3 ?? objInfo.name
                : lang === "fr"
                ? objInfo.name4 ?? objInfo.name
                : lang === "es"
                ? objInfo.name5 ?? objInfo.name
                : objInfo.name2 ?? objInfo.name1 ?? objInfo.name;
            if (localizedName) headerName = localizedName;
            else {
              const translationKey = `widgets.${field}`;
              headerName = t(translationKey, { defaultValue: field });
            }
          } else {
            const translationKey = `widgets.${field}`;
            headerName = t(translationKey, { defaultValue: field });
          }
        } else {
          const translationKey = `widgets.${field}`;
          headerName = t(translationKey, { defaultValue: field });
        }
        return {
          field,
          headerName,
          flex: 1,
          minWidth: 90,
          sortable: false,
          headerAlign: IS_VENDAI ? "left" : "center",
          align: IS_VENDAI ? "left" : "center",
          renderCell: (params) => {
            const value = params.value;
            return (
              <Typography
                variant="body2"
                sx={{
                  width: "100%",
                  textAlign: IS_VENDAI ? "left" : "center",
                  fontSize: "0.75rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {typeof value === "number"
                  ? value.toLocaleString()
                  : value ?? "-"}
              </Typography>
            );
          },
        };
      });
    return [dateColumn, ...dynamicColumns];
  }, [widget.targetObjectList, t, objectData, i18n.language]);

  const convertValue = useMemo(
    () => (codeStr, rawVal) => {
      if (rawVal === null || rawVal === undefined) return null;
      if (codeStr === "NID") return rawVal;
      const numericCode = parseInt(codeStr, 10);
      if (isNaN(numericCode)) return rawVal;
      const objInfo = objectData.find((o) => o.code === numericCode);
      if (!objInfo) return rawVal;
      if (
        widget.isConvertableObject === false ||
        objInfo.isConvertableObject === false
      ) {
        return rawVal;
      }
      try {
        return ConvertOIDValueToString({ Type: objInfo.type }, rawVal);
      } catch (e) {
        return rawVal;
      }
    },
    [objectData, widget.isConvertableObject]
  );

  useEffect(() => {
    let intervalId;
    async function fetchList() {
      setLoading(true);
      setError(null);
      if (!nodeId) {
        setRows([]);
        setLoading(false);
        return;
      }
      try {
        const data = await getAggregation(
          widget.request.payload,
          widget.request.endpoint,
          nodeId
        );
        let records = [];
        if (Array.isArray(data)) {
          records = data;
        } else if (data && Array.isArray(data.rows)) {
          records = data.rows;
        } else if (data && Array.isArray(data.data)) {
          records = data.data;
        } else {
          records = [];
        }
        records.sort((a, b) => {
          const getEpoch = (rec) => {
            let v = rec?.epoch ?? rec?.Epoch ?? rec?.timestamp ?? rec?.time;
            if (v === undefined || v === null || v === "") return -Infinity;
            let num = Number(v);
            if (Number.isNaN(num)) return -Infinity;
            if (num < 1e12) num *= 1000;
            return num;
          };
          return getEpoch(b) - getEpoch(a);
        });
        const getValueFromRecord = (rec, code) => {
          if (!rec || typeof rec !== "object") return null;
          if (Object.prototype.hasOwnProperty.call(rec, code)) {
            return rec[code];
          }
          const numCode = Number(code);
          if (!Number.isNaN(numCode) && Array.isArray(rec.paramList)) {
            const found = rec.paramList.find((p) => Number(p?.oid) === numCode);
            if (found && found.val !== undefined) return found.val;
          }
          return null;
        };

        const coerceNumberIfPossible = (v) => {
          if (v === null || v === undefined) return v;
          if (typeof v === "number") return v;
          if (typeof v === "string" && v.trim() !== "") {
            const n = Number(v);
            if (!Number.isNaN(n)) return n;
          }
          return v;
        };

        const processed = records.map((rec, idx) => {
          const rawEpoch =
            rec?.epoch ?? rec?.Epoch ?? rec?.timestamp ?? rec?.time;
          const id = rec?._id ?? `${rec?.NID ?? "n"}-${rawEpoch ?? "t"}-${idx}`;
          const row = { id };

          if (rawEpoch !== undefined && rawEpoch !== null && rawEpoch !== "") {
            let epochNum = Number(rawEpoch);
            if (!Number.isNaN(epochNum)) {
              if (epochNum < 1e12) epochNum *= 1000;
              const d = new Date(epochNum);
              const isValid = !isNaN(d.getTime());
              row.date = isValid
                ? d.toLocaleString(undefined, {
                    year: "2-digit",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-";
            } else {
              row.date = "-";
            }
          } else {
            row.date = null;
          }

          if (Array.isArray(widget.targetObjectList)) {
            widget.targetObjectList.forEach((code) => {
              if (code === "NID") return;
              const rawVal = getValueFromRecord(rec, code);
              const maybeNumber = coerceNumberIfPossible(rawVal);
              row[code] = convertValue(code, maybeNumber);
            });
          }
          return row;
        });
        setRows(processed);
      } catch (e) {
        console.error("WidgetList fetch error", e);
        setError(e.message || "Veri alınamadı");
        setRows([]);
      } finally {
        setLoading(false);
      }
    }
    fetchList();
    // Paused: bir kez çekildi, tekrarlı polling kurulmaz.
    if (!isRunning) return undefined;
    intervalId = setInterval(fetchList, intervalMs);
    return () => clearInterval(intervalId);
  }, [
    widget,
    getAggregation,
    objectData,
    convertValue,
    nodeId,
    intervalMs,
    isRunning,
  ]);

  return (
    <Paper
      elevation={2}
      sx={(theme) => ({
        p: 0.5,
        borderRadius: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginBottom: 2,
        backgroundColor: theme.palette.background.paper,
        position: "relative",
        border: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Typography
        variant="h4"
        align="center"
        sx={{
          px: 1,
          py: 0.5,
          fontWeight: 600,
          textAlign: "center",
          width: "100%",
        }}
        color="text.primary"
      >
        {t(`widgets.${widget.headKey}`, widget.headKey)}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: HEADER_HEIGHT + ROW_HEIGHT * VISIBLE_ROWS,
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          hideFooter
          density="compact"
          loading={loading}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          sx={{
            height: "100%",
            overflow: "unset",
            border: "none",
            "& .MuiDataGrid-main": {
              height: "100%",
            },
            "& .MuiDataGrid-virtualScroller": {
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
            },
            "& .MuiDataGrid-columnHeaders": {
              color: (theme) => theme.palette.text.primary,
              minHeight: HEADER_HEIGHT,
              maxHeight: HEADER_HEIGHT,
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
              padding: "0 4px",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark" ? "#29434e" : "#e0e0e0"
                }`,
              display: "flex",
              alignItems: "center",
              justifyContent: IS_VENDAI ? "flex-start" : "center",
              lineHeight: 1.1,
            },
            "& .MuiDataGrid-row": {
              alignItems: "center !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              width: "100%",
              textAlign: IS_VENDAI ? "left" : "center",
              lineHeight: 1.2,
            },
          }}
          getRowId={(row) => row.id}
        />
        {error && !loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.4)"
                  : "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              fontWeight: 500,
              color: "error.main",
            }}
          >
            {error}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default function ObjectDetails() {
  const [widgetData, setWidgetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const rawId = getPlaceIdFromPathname(location.pathname);
  const nodeId = rawId ? parseInt(rawId, 10) : null;

  // Use custom hook to get objectData from localStorage
  const objectData = useObjectData();

  useEffect(() => {
    async function fetchWidgets() {
      setLoading(true);
      try {
        const projectCode = localStorage.getItem("projectCode");
        const projectData = await getProjects();
        const currentProject = projectData.find(
          (project) => project.code === projectCode
        );

        if (currentProject && currentProject.projectDetailWidgetJson) {
          const widgets = JSON.parse(currentProject.projectDetailWidgetJson);
          setWidgetData(widgets);
        } else {
          console.error("No widgets found for the current project.");
          setWidgetData({ widgets: [] });
        }
      } catch (error) {
        console.error("Error fetching widgets:", error);
        setWidgetData({ widgets: [] });
      } finally {
        setLoading(false);
      }
    }
    fetchWidgets();
  }, []);

  const hasWidgets =
    widgetData &&
    Array.isArray(widgetData.widgets) &&
    widgetData.widgets.length > 0;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {hasWidgets && (
        <Box>
          {widgetData.widgets.map((row, rowIndex) => (
            <Grid container spacing={{ xs: 1, sm: 2 }} key={`row-${rowIndex}`}>
              {row.map((widget, colIndex) => (
                <Grid
                  item
                  xs={12}
                  sm={12 / row.length}
                  key={`widget-${rowIndex}-${colIndex}`}
                >
                  {widget.widgetType === "box" && (
                    <WidgetBox
                      widget={widget}
                      getAggregation={getAggregation}
                      objectData={objectData}
                      nodeId={nodeId}
                    />
                  )}
                  {widget.widgetType === "table" && (
                    <WidgetTable
                      widget={widget}
                      getAggregation={getAggregation}
                      objectData={objectData}
                    />
                  )}
                  {widget.widgetType === "list" && (
                    <WidgetList
                      widget={widget}
                      getAggregation={getAggregation}
                      objectData={objectData}
                      nodeId={nodeId}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          ))}
        </Box>
      )}
    </>
  );
}
